import { useEffect, useState } from 'react';
import { Modal, Select, TimePicker, message } from 'antd';
import dayjs from 'dayjs';
import {
    hasCourtScheduleConflict,
    minutesToScheduleTime,
    scheduleCourtId,
    scheduleTimeToMinutes,
    type CourtEvents,
    type ScheduleBoardEvent,
} from './scheduleUtils';

interface ScheduleEditEventModalProps {
    visible: boolean;
    event: ScheduleBoardEvent | null;
    maxCourtCount: number;
    events: CourtEvents;
    validStartTime: number;
    validEndTime: number;
    onCancel: () => void;
    onConfirm: (event: ScheduleBoardEvent) => void;
}

const ScheduleEditEventModal: React.FC<ScheduleEditEventModalProps> = ({
    visible,
    event,
    maxCourtCount,
    events,
    validStartTime,
    validEndTime,
    onCancel,
    onConfirm,
}) => {
    const [messageApi, contextHolder] = message.useMessage();
    const [startTime, setStartTime] = useState<dayjs.Dayjs | null>(null);
    const [court, setCourt] = useState('');

    useEffect(() => {
        if (!visible || !event) {
            return;
        }
        setCourt(event.courtId);
        setStartTime(
            event.end > event.start
                ? dayjs(minutesToScheduleTime(event.start), 'HH:mm')
                : null,
        );
    }, [visible, event]);

    const endTimeDisplay = () => {
        if (!startTime || !event) return 'Select start time';
        const start = scheduleTimeToMinutes(startTime.format('HH:mm'));
        return minutesToScheduleTime(start + event.duration);
    };

    const handleOk = () => {
        if (!event || !startTime) return;

        const start = scheduleTimeToMinutes(startTime.format('HH:mm'));
        const end = start + event.duration;

        if (start < validStartTime || end > validEndTime) {
            messageApi.error('Selected time is out of valid range');
            return;
        }

        if (hasCourtScheduleConflict(events, court, event.id, start, end)) {
            messageApi.error('Time slot is already occupied');
            return;
        }

        onConfirm({
            ...event,
            start,
            end,
            courtId: court,
        });
    };

    return (
        <>
            {contextHolder}
            <Modal
                title={`${event && event.end > event.start ? 'Edit' : 'Add'} ${event?.title || ''}`}
                open={visible}
                onOk={handleOk}
                onCancel={onCancel}
                width={400}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                        <div style={{ marginBottom: '8px', color: '#666' }}>Court</div>
                        <Select
                            style={{ width: '100%' }}
                            value={court}
                            onChange={setCourt}
                        >
                            {Array.from({ length: maxCourtCount }, (_, i) => (
                                <Select.Option key={scheduleCourtId(i + 1)} value={scheduleCourtId(i + 1)}>
                                    COURT {i + 1}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <div style={{ marginBottom: '8px', color: '#666' }}>Start Time</div>
                        <TimePicker
                            format="HH:mm"
                            minuteStep={10}
                            value={startTime}
                            onChange={setStartTime}
                            needConfirm={false}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '16px' }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ marginBottom: '8px', color: '#666' }}>End Time</div>
                            <div style={{
                                padding: '8px 12px',
                                border: '1px solid #d9d9d9',
                                borderRadius: '6px',
                                background: '#f5f5f5',
                                color: !startTime ? '#999' : '#333',
                            }}>
                                {endTimeDisplay()}
                            </div>
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ marginBottom: '8px', color: '#666' }}>Duration</div>
                            <div style={{
                                padding: '8px 12px',
                                border: '1px solid #d9d9d9',
                                borderRadius: '6px',
                                background: '#f5f5f5',
                                color: '#333',
                            }}>
                                {event?.duration} Minutes
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default ScheduleEditEventModal;
